import axios from 'axios';
import qs from 'query-string';
import { compose, forEach, fromPairs, isEmpty, join, map, prop, toPairs } from 'ramda';

let globalConfig = { debug: true };
const endPoint = (path, config = globalConfig) => `${config.endpoint}${path}`;
const SCOPES = ['AUTHENTICATED', 'APPROVED'];

const digitalInteractionsV1 = ({ data, auth: { accessToken } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  return axios.post(endPoint('/public/v3/interactions'), data, { headers }).then(prop('data'));
};

const digitalInteractionsV4 = ({ data, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.post(endPoint('/public/v4/interactions'), data, { headers }).then(prop('data'));
};

const digitalInteractionsV5 = ({ data, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.post(endPoint('/public/v5/interactions'), data, { headers }).then(prop('data'));
};

const createMissionV1 = ({ claimId, data, auth: { accessToken } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  return axios.post(endPoint(`/public/v3/claims/${claimId}/missions`), data, { headers }).then(prop('data'));
};

const createMissionV4 = ({ claimId, data, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.post(endPoint(`/public/v4/claims/${claimId}/missions`), data, { headers }).then(prop('data'));
};

const createMissionV5 = ({ claimId, data, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.post(endPoint(`/public/v5/claims/${claimId}/missions`), data, { headers }).then(prop('data'));
};

const createRentalMissionV4 = ({ claimId, data, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .post(endPoint(`/public/v4/claims/${claimId}/missions/rental_mission`), data, { headers })
    .then(prop('data'));
};

const createRentalMissionV5 = ({ claimId, data, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .post(endPoint(`/public/v5/claims/${claimId}/missions/rental_mission`), data, { headers })
    .then(prop('data'));
};

const updateRentalMissionV4 = ({ claimId, missionId, data, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .put(endPoint(`/public/v4/claims/${claimId}/missions/${missionId}/rental_mission`), data, { headers })
    .then(prop('data'));
};

const updateRentalMissionV5 = ({ claimId, missionId, data, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .put(endPoint(`/public/v5/claims/${claimId}/missions/${missionId}/rental_mission`), data, { headers })
    .then(prop('data'));
};

const extendRentalMission = ({ version, claimId, missionId, data, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .post(endPoint(`/public/${version}/claims/${claimId}/missions/${missionId}/extend_rental`), data, { headers })
    .then(prop('data'));
};

const getAllMissions = ({ claimId, auth: { accessToken } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  return axios.get(endPoint(`/public/v3/claims/${claimId}/missions`), { headers }).then(prop('data'));
};

const getAllMissionsV4 = ({ claimId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.get(endPoint(`/public/v4/claims/${claimId}/missions`), { headers }).then(prop('data'));
};

const getAllMissionsV5 = ({ claimId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.get(endPoint(`/public/v5/claims/${claimId}/missions`), { headers }).then(prop('data'));
};

// Claim + Mission History
const getClaimHistoryV4 = ({ claimId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.get(endPoint(`/public/v4/claims/${claimId}/history`), { headers }).then(prop('data'));
};

const getClaimHistoryV5 = ({ claimId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.get(endPoint(`/public/v5/claims/${claimId}/history`), { headers }).then(prop('data'));
};

const cancelMissionV1 = ({ claimId, missionId, auth: { accessToken } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  return axios.delete(endPoint(`/public/v3/claims/${claimId}/missions/${missionId}`), { headers }).then(prop('data'));
};

const cancelMissionV4 = ({ claimId, missionId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.delete(endPoint(`/public/v4/claims/${claimId}/missions/${missionId}`), { headers }).then(prop('data'));
};

const cancelMissionV5 = ({ claimId, missionId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.delete(endPoint(`/public/v5/claims/${claimId}/missions/${missionId}`), { headers }).then(prop('data'));
};

const cancelMissionCostV4 = ({ claimId, missionId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };

  return axios
    .get(endPoint(`/public/v4/claims/${claimId}/missions/${missionId}/cancel_mission_cost`), { headers })
    .then(prop('data'));
};

const cancelMissionCostV5 = ({ claimId, missionId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };

  return axios
    .get(endPoint(`/public/v5/claims/${claimId}/missions/${missionId}/cancel_mission_cost`), { headers })
    .then(prop('data'));
};

const automaticCancelMissionV4 = ({ claimId, missionId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };

  const data = {
    reasonCode: 'CUSTOMER_REQUESTED_CANCELLATION',
  };

  const url = endPoint(`/public/v4/claims/${claimId}/missions/${missionId}/cancel_mission`);

  return axios.post(url, data, { headers }).then(prop('data'));
};

const automaticCancelMissionV5 = ({ claimId, missionId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };

  const data = {
    reasonCode: 'CUSTOMER_REQUESTED_CANCELLATION',
  };

  const url = endPoint(`/public/v5/claims/${claimId}/missions/${missionId}/cancel_mission`);

  return axios.post(url, data, { headers }).then(prop('data'));
};

const putVehicleDestination = ({ data, missionId, claimId, auth: { accessToken } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  return axios
    .put(endPoint(`/public/v3/claims/${claimId}/missions/${missionId}/vehicle_destination`), data, { headers })
    .then(prop('data'));
};

const putVehicleDestinationV4 = ({ data, missionId, claimId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .put(endPoint(`/public/v4/claims/${claimId}/missions/${missionId}/vehicle_destination`), data, { headers })
    .then(prop('data'));
};

const putVehicleDestinationV5 = ({ data, missionId, claimId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .put(endPoint(`/public/v5/claims/${claimId}/missions/${missionId}/vehicle_destination`), data, { headers })
    .then(prop('data'));
};

const startMissionMonitoring = ({ claimId, onMessage, onError, auth: { accessToken } }) => {
  const eventSource = new EventSource(
    endPoint(`/public/v3/claims/${claimId}/missions/events?access_token=${accessToken}`),
  );
  eventSource.onerror = onError;
  eventSource.onmessage = ({ data }) => onMessage(JSON.parse(data));
  return eventSource;
};

const startMissionMonitoringV4 = ({ claimId, onMessage, onError, auth: { accessToken, jwt } }) => {
  const eventSource = new EventSource(
    endPoint(`/public/v4/claims/${claimId}/missions/events?access_token=${accessToken}&jwt=${jwt}`),
  );
  eventSource.onerror = onError;
  eventSource.onmessage = ({ data }) => onMessage(JSON.parse(data));
  return eventSource;
};

const startMissionMonitoringV5 = ({ claimId, onMessage, onError, auth: { accessToken, jwt } }) => {
  const eventSource = new EventSource(
    endPoint(`/public/v5/claims/${claimId}/missions/events?access_token=${accessToken}&jwt=${jwt}`),
  );
  eventSource.onerror = onError;
  eventSource.onmessage = ({ data }) => onMessage(JSON.parse(data));
  return eventSource;
};

const getCarDestinationsV1 = ({ missionId, claimId, auth: { accessToken } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  return axios
    .get(endPoint(`/public/v3/claims/${claimId}/missions/${missionId}/vehicle_destinations/search`), { headers })
    .then(prop('data'))
    .catch(error => {
      if (error.response && error.response.status === 404 && error.response.data && isEmpty(error.response.data))
        return [];
      throw error;
    });
};

const getCarDestinationsV4 = ({ missionId, claimId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .get(endPoint(`/public/v4/claims/${claimId}/missions/${missionId}/vehicle_destinations/search`), { headers })
    .then(prop('data'))
    .catch(error => {
      if (error.response && error.response.status === 404 && error.response.data && isEmpty(error.response.data))
        return [];
      throw error;
    });
};

const getCarDestinationsV5 = ({ missionId, claimId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .get(endPoint(`/public/v5/claims/${claimId}/missions/${missionId}/vehicle_destinations/search`), { headers })
    .then(prop('data'))
    .catch(error => {
      if (error.response && error.response.status === 404 && error.response.data && isEmpty(error.response.data))
        return [];
      throw error;
    });
};

const getSpecificCarDestinationsV4 = ({ missionId, claimId, data, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };

  return axios
    .post(
      endPoint(`/public/v4/claims/${claimId}/missions/${missionId}/vehicle_destinations/search_specific_location`),
      data,
      { headers },
    )
    .then(prop('data'))
    .catch(error => {
      if (error.response && error.response.status === 404 && error.response.data && isEmpty(error.response.data))
        return [];
      throw error;
    });
};

const getSpecificCarDestinationsV5 = ({ data, claimId, missionId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };

  return axios
    .post(
      endPoint(`/public/v5/claims/${claimId}/missions/${missionId}/vehicle_destinations/search_specific_location`),
      data,
      { headers },
    )
    .then(prop('data'))
    .catch(error => {
      if (error.response && error.response.status === 404 && error.response.data && isEmpty(error.response.data))
        return [];
      throw error;
    });
};

const putClaimLocation = ({ data, claimId, auth: { accessToken } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  return axios.put(endPoint(`/public/v3/claims/${claimId}/location`), data, { headers }).then(prop('data'));
};

const putClaimLocationV4 = ({ data, claimId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.put(endPoint(`/public/v4/claims/${claimId}/location`), data, { headers }).then(prop('data'));
};

const putClaimLocationV5 = ({ data, claimId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.put(endPoint(`/public/v5/claims/${claimId}/location`), data, { headers }).then(prop('data'));
};

const searchClaimV1 = ({ data: { plateNumber }, auth: { accessToken } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  return axios.get(endPoint(`/public/v3/claims/search?plate_number=${plateNumber}`), { headers }).then(prop('data'));
};

const createSearchParams = ({ plateNumber, vin, phoneNumber }) => {
  const params = new URLSearchParams();
  if (phoneNumber) {
    params.append('phone_number', phoneNumber);
  }
  if (vin) {
    params.append('vin', vin);
  }
  if (plateNumber) {
    params.append('plate_number', plateNumber);
  }

  return params.toString();
};

const searchClaimV4 = ({ data: { plateNumber, vin, phoneNumber }, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };

  return axios
    .get(endPoint(`/public/v4/claims/search?${createSearchParams({ plateNumber, vin, phoneNumber })}`), { headers })
    .then(prop('data'));
};

const searchClaimV5 = ({ data: { plateNumber, vin, phoneNumber }, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };

  return axios
    .get(endPoint(`/public/v5/claims/search?${createSearchParams({ plateNumber, vin, phoneNumber })}`), { headers })
    .then(prop('data'));
};

const searchClaimByIdV1 = ({ data: { id }, auth: { accessToken } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  return axios.get(endPoint(`/public/v3/claims/${id}`), { headers }).then(prop('data'));
};

const searchClaimByIdV4 = ({ data: { id }, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.get(endPoint(`/public/v4/claims/${id}`), { headers }).then(prop('data'));
};

const searchClaimByIdV5 = ({ data: { id }, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.get(endPoint(`/public/v5/claims/${id}`), { headers }).then(prop('data'));
};

const npsEligibilityClaim = ({ claimId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.get(endPoint(`/public/v4/claims/${claimId}/nps_eligibility`), { headers }).then(prop('data'));
};

const npsEligibilityClaimV5 = ({ claimId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.get(endPoint(`/public/v5/claims/${claimId}/nps_eligibility`), { headers }).then(prop('data'));
};

const putNpsEligibilityClaimV4 = ({ auth: { accessToken, jwt }, claimId, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.put(endPoint(`/public/v4/claims/${claimId}/nps`), data, { headers }).then(prop('data'));
};

const putNpsEligibilityClaimV5 = ({ auth: { accessToken, jwt }, claimId, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.put(endPoint(`/public/v5/claims/${claimId}/nps`), data, { headers }).then(prop('data'));
};

const authenticate = () => {
  const params = {
    grant_type: 'client_credentials',
    client_id: globalConfig.clientId,
    client_secret: globalConfig.clientSecret,
    scope: join(' ', SCOPES),
  };

  const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
  const query = qs.stringify(params);
  return axios.post(endPoint('/oauth/token'), query, { headers }).then(prop('data'));
};

const refreshToken = ({ auth: { refreshToken: token } }) => {
  const params = {
    grant_type: 'refresh_token',
    client_id: globalConfig.clientId,
    client_secret: globalConfig.clientSecret,
    scope: SCOPES,
    refresh_token: token,
  };

  const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
  const query = qs.stringify(params);
  return axios.post(endPoint('/oauth/token'), query, { headers }).then(prop('data'));
};

const getBrandSuggestionsV1 = ({ auth: { accessToken }, hint }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  return axios
    .get(
      endPoint(
        `/public/v3/vehicles/makes_suggestion?make_hint=${hint}&max_suggestions=${globalConfig.maxBrandSuggestions}`,
      ),
      { headers },
    )
    .then(prop('data'));
};

const getModelSuggestionsV1 = ({ auth: { accessToken }, brand }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  return axios
    .get(
      endPoint(
        `/public/v3/vehicles/models_suggestion?make_hint=${brand}&max_suggestions=${globalConfig.maxModelSuggestions}`,
      ),
      { headers },
    )
    .then(prop('data'));
};

const getBrandSuggestionsV4 = ({ auth: { accessToken, jwt }, hint }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .get(
      endPoint(
        `/public/v4/vehicles/makes_suggestion?make_hint=${hint}&max_suggestions=${globalConfig.maxBrandSuggestions}`,
      ),
      { headers },
    )
    .then(prop('data'));
};

const getBrandSuggestionsV5 = ({ auth: { accessToken, jwt }, hint }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .get(
      endPoint(
        `/public/v5/vehicles/makes_suggestion?make_hint=${hint}&max_suggestions=${globalConfig.maxBrandSuggestions}`,
      ),
      { headers },
    )
    .then(prop('data'));
};

const getModelSuggestionsV4 = ({ auth: { accessToken, jwt }, brand }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .get(
      endPoint(
        `/public/v4/vehicles/models_suggestion?make_hint=${brand}&max_suggestions=${globalConfig.maxModelSuggestions}`,
      ),
      { headers },
    )
    .then(prop('data'));
};

const getModelSuggestionsV5 = ({ auth: { accessToken, jwt }, brand }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .get(
      endPoint(
        `/public/v5/vehicles/models_suggestion?make_hint=${brand}&max_suggestions=${globalConfig.maxModelSuggestions}`,
      ),
      { headers },
    )
    .then(prop('data'));
};

const getHighwaySectionsV1 = ({ lat, lng, metres, auth: { accessToken } }) => {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };
  return axios
    .get(
      endPoint(`/public/v3/localisation/highway_sections/search?latitude=${lat}&longitude=${lng}&metres=${metres}`),
      { headers },
    )
    .then(prop('data'));
};

const getHighwaySectionsV4 = ({ lat, lng, metres, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .get(
      endPoint(`/public/v4/localisation/highway_sections/search?latitude=${lat}&longitude=${lng}&metres=${metres}`),
      { headers },
    )
    .then(prop('data'));
};

const getHighwaySectionsV5 = ({ lat, lng, metres, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .get(
      endPoint(`/public/v5/localisation/highway_sections/search?latitude=${lat}&longitude=${lng}&metres=${metres}`),
      { headers },
    )
    .then(prop('data'));
};

const searchVehicleInfoV1 = ({ plateNumber, auth: { accessToken } }) => {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };
  return axios.get(endPoint(`/public/v3/vehicles/search/?plate_number=${plateNumber}`), { headers }).then(prop('data'));
};

const searchVehicleInfoV4 = ({ plateNumber, vin, isVinMandatory, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  let params = `plate_number=${plateNumber}`;

  if (vin) params += `&vin=${vin}`;
  if (isVinMandatory) params += `&isVinMandatory=${isVinMandatory}`;

  return axios.get(endPoint(`/public/v4/vehicles/search/?${params}`), { headers }).then(prop('data'));
};

const searchVehicleInfoV5 = ({ plateNumber, vin, isVinMandatory, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  let params = `plate_number=${plateNumber}`;

  if (vin) params += `&vin=${vin}`;
  if (isVinMandatory) params += `&isVinMandatory=${isVinMandatory}`;

  return axios.get(endPoint(`/public/v5/vehicles/search/?${params}`), { headers }).then(prop('data'));
};

const putClaimVehicleLossV1 = ({ claimId, data, auth: { accessToken } }) => {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };
  return axios.put(endPoint(`/public/v3/claims/${claimId}/vehicle_loss`), data, { headers }).then(prop('data'));
};

const putClaimVehicleLossV4 = ({ claimId, data, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.put(endPoint(`/public/v4/claims/${claimId}/vehicle_loss`), data, { headers }).then(prop('data'));
};

const putClaimVehicleLossV5 = ({ claimId, data, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.put(endPoint(`/public/v5/claims/${claimId}/vehicle_loss`), data, { headers }).then(prop('data'));
};

const getInsuranceCompaniesV1 = ({ auth: { accessToken }, name }) => {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };
  return axios.get(endPoint(`/public/v3/insurance_companies/suggestion?name=${name}`), { headers }).then(prop('data'));
};

const getInsuranceCompaniesV4 = ({ auth: { accessToken, jwt }, name }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.get(endPoint(`/public/v4/insurance_companies/suggestion?name=${name}`), { headers }).then(prop('data'));
};

const getInsuranceCompaniesV5 = ({ auth: { accessToken, jwt }, name }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.get(endPoint(`/public/v5/insurance_companies/suggestion?name=${name}`), { headers }).then(prop('data'));
};

const createClaimDigitalExit = ({ auth: { accessToken }, claimId, data }) => {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };
  return axios.put(endPoint(`/public/v3/claims/${claimId}/digital_exit`), data, { headers }).then(prop('data'));
};

const createClaimDigitalExitV4 = ({ auth: { accessToken, jwt }, claimId, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.put(endPoint(`/public/v4/claims/${claimId}/digital_exit`), data, { headers }).then(prop('data'));
};

const createClaimDigitalExitV5 = ({ auth: { accessToken, jwt }, claimId, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.put(endPoint(`/public/v5/claims/${claimId}/digital_exit`), data, { headers }).then(prop('data'));
};

const createMissionDigitalExit = ({ auth: { accessToken }, claimId, missionId, data }) => {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };
  return axios
    .put(endPoint(`/public/v3/claims/${claimId}/missions/${missionId}/digital_exit`), data, { headers })
    .then(prop('data'));
};

const createMissionDigitalExitV4 = ({ auth: { accessToken, jwt }, claimId, missionId, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .put(endPoint(`/public/v4/claims/${claimId}/missions/${missionId}/digital_exit`), data, { headers })
    .then(prop('data'));
};

const createMissionDigitalExitV5 = ({ auth: { accessToken, jwt }, claimId, missionId, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .put(endPoint(`/public/v5/claims/${claimId}/missions/${missionId}/digital_exit`), data, { headers })
    .then(prop('data'));
};

const createClaimV1 = ({ data, auth: { accessToken } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  return axios.post(endPoint('/public/v3/claims'), data, { headers }).then(prop('data'));
};

const createClaimV4 = ({ data, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.post(endPoint('/public/v4/claims'), data, { headers }).then(prop('data'));
};

const createClaimV5 = ({ data, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.post(endPoint('/public/v5/claims'), data, { headers }).then(prop('data'));
};

const putClaimEvent = ({ data, claimId, auth: { accessToken } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  return axios.put(endPoint(`/public/v3/claims/${claimId}/event`), data, { headers }).then(prop('data'));
};

const putClaimEventV4 = ({ data, claimId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.put(endPoint(`/public/v4/claims/${claimId}/event`), data, { headers }).then(prop('data'));
};

const putClaimEventV5 = ({ data, claimId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.put(endPoint(`/public/v5/claims/${claimId}/event`), data, { headers }).then(prop('data'));
};

const dispatchMission = ({ auth: { accessToken }, claimId, missionId }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  return axios
    .post(endPoint(`/public/v3/claims/${claimId}/missions/${missionId}/dispatch`), undefined, { headers })
    .then(prop('data'));
};

const dispatchMissionV4 = ({ auth: { accessToken, jwt }, claimId, missionId }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .post(endPoint(`/public/v4/claims/${claimId}/missions/${missionId}/dispatch`), undefined, { headers })
    .then(prop('data'));
};

const dispatchMissionV5 = ({ auth: { accessToken, jwt }, claimId, missionId }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .post(endPoint(`/public/v5/claims/${claimId}/missions/${missionId}/dispatch`), undefined, { headers })
    .then(prop('data'));
};

const updateMissionV4 = ({ auth: { accessToken, jwt }, claimId, missionId, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .put(endPoint(`/public/v4/claims/${claimId}/missions/${missionId}`), data, { headers })
    .then(prop('data'));
};

const updateMissionV5 = ({ auth: { accessToken, jwt }, claimId, missionId, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .put(endPoint(`/public/v4/claims/${claimId}/missions/${missionId}`), data, { headers })
    .then(prop('data'));
};

const updateMissionType = ({ claimId, missionId, auth: { accessToken }, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  return axios
    .put(endPoint(`/public/v3/claims/${claimId}/missions/${missionId}/mission_type`), data, { headers })
    .then(prop('data'));
};

const updateMissionTypeV4 = ({ claimId, missionId, auth: { accessToken, jwt }, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .put(endPoint(`/public/v4/claims/${claimId}/missions/${missionId}/mission_type`), data, { headers })
    .then(prop('data'));
};

const updateMissionTypeV5 = ({ claimId, missionId, auth: { accessToken, jwt }, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .put(endPoint(`/public/v5/claims/${claimId}/missions/${missionId}/mission_type`), data, { headers })
    .then(prop('data'));
};

const getTowLimits = ({ claimId, missionId, auth: { accessToken } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  return axios
    .get(endPoint(`/public/v3/claims/${claimId}/missions/${missionId}/tow_limits`), { headers })
    .then(prop('data'));
};

const getTowLimitsV4 = ({ claimId, missionId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .get(endPoint(`/public/v4/claims/${claimId}/missions/${missionId}/tow_limits`), { headers })
    .then(prop('data'));
};

const getTowLimitsV5 = ({ claimId, missionId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .get(endPoint(`/public/v5/claims/${claimId}/missions/${missionId}/tow_limits`), { headers })
    .then(prop('data'));
};

const getRosLimitsV4 = ({ claimId, missionId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .get(endPoint(`/public/v4/claims/${claimId}/missions/${missionId}/ros_limits`), { headers })
    .then(prop('data'));
};

const getRosLimitsV5 = ({ claimId, missionId, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .get(endPoint(`/public/v5/claims/${claimId}/missions/${missionId}/ros_limits`), { headers })
    .then(prop('data'));
};

const getHashedClaimId = ({ claimId, auth: { accessToken } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  return axios.get(endPoint(`/public/v3/claims/${claimId}/hashedId`), { headers }).then(prop('data'));
};

const requestOTP = ({ auth: { accessToken }, data, captchaToken }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Captcha-Token': captchaToken,
  };
  return axios.post(endPoint(`/public/v4/otp`), data, { headers }).then(prop('data'));
};

const requestOTPV5 = ({ auth: { accessToken }, data, captchaToken }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Captcha-Token': captchaToken,
  };
  return axios.post(endPoint(`/public/v5/otp`), data, { headers }).then(prop('data'));
};

const getJWT = ({ auth: { accessToken }, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  return axios.post(endPoint(`/public/v4/token`), data, { headers }).then(prop('data'));
};

const getJWTV5 = ({ auth: { accessToken }, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  return axios.post(endPoint(`/public/v5/token`), data, { headers }).then(prop('data'));
};

const getTrustedJWT = ({ auth: { accessToken }, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  return axios.post(endPoint(`/public/v4/trusted_token`), data, { headers }).then(prop('data'));
};

const getTrustedJWTV5 = ({ auth: { accessToken }, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
  return axios.post(endPoint(`/public/v5/trusted_token`), data, { headers }).then(prop('data'));
};

const getProductConfigurationV4 = ({ productCode, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.get(endPoint(`/public/v4/products/${productCode}`), { headers }).then(prop('data'));
};

const getProductConfigurationV5 = ({ productCode, auth: { accessToken, jwt } }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios.get(endPoint(`/public/v5/products/${productCode}`), { headers }).then(prop('data'));
};

const checkCscEligibilityV4 = ({ auth: { accessToken, jwt }, claimId, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .post(endPoint(`/public/v4/claims/${claimId}/check_csc_eligibility`), data, { headers })
    .then(prop('data'));
};

const checkCscEligibilityV5 = ({ auth: { accessToken, jwt }, claimId, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .post(endPoint(`/public/v5/claims/${claimId}/check_csc_eligibility`), data, { headers })
    .then(prop('data'));
};

const updateImmobilizationGarageV4 = ({ auth: { accessToken, jwt }, claimId, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .post(endPoint(`/public/v4/claims/${claimId}/update_immobilisation_garage`), data, { headers })
    .then(prop('data'));
};

const updateImmobilizationGarageV5 = ({ auth: { accessToken, jwt }, claimId, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .post(endPoint(`/public/v5/claims/${claimId}/update_immobilisation_garage`), data, { headers })
    .then(prop('data'));
};

const rentalCurrentLimitsV4 = ({ auth: { accessToken, jwt }, claimId, missionId }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .get(endPoint(`/public/v4/claims/${claimId}/missions/${missionId}/rental_current_limits`), { headers })
    .then(prop('data'));
};

const rentalCurrentLimitsV5 = ({ auth: { accessToken, jwt }, claimId, missionId }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };
  return axios
    .get(endPoint(`/public/v5/claims/${claimId}/missions/${missionId}/rental_current_limits`), { headers })
    .then(prop('data'));
};

const rentalExtensionInfo = ({ version, auth: { accessToken, jwt }, claimId, missionId, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    'Authorization-Person': jwt,
  };

  return axios
    .put(endPoint(`/public/${version}/claims/${claimId}/missions/${missionId}/rental_extension_info`), data, {
      headers,
    })
    .then(prop('data'));
};

const config = config => (globalConfig = { ...globalConfig, ...config }); // eslint-disable-line no-shadow

const methods = {
  config,
  authenticate,
  refreshToken,
  createClaimV1,
  getAllMissions,
  startMissionMonitoring,
  putClaimVehicleLossV1,
  createClaimDigitalExit,
  createMissionDigitalExit,
  putClaimLocation,
  putClaimEvent,
  searchClaimByIdV1,
  getHighwaySectionsV1,
  createMissionV1,
  cancelMissionV1,
  putVehicleDestination,
  dispatchMission,
  updateMissionType,
  getTowLimits,
  getCarDestinationsV1,
  searchVehicleInfoV1,
  getBrandSuggestionsV1,
  getModelSuggestionsV1,
  digitalInteractionsV1,
  getInsuranceCompaniesV1,
  searchClaimV1,
  getHashedClaimId,
  createClaimV4,
  searchClaimV4,
  digitalInteractionsV4,
  createMissionV4,
  getAllMissionsV4,
  cancelMissionV4,
  cancelMissionCostV4,
  automaticCancelMissionV4,
  putVehicleDestinationV4,
  getCarDestinationsV4,
  getSpecificCarDestinationsV4,
  putClaimLocationV4,
  searchClaimByIdV4,
  getBrandSuggestionsV4,
  getModelSuggestionsV4,
  getHighwaySectionsV4,
  searchVehicleInfoV4,
  putClaimVehicleLossV4,
  getInsuranceCompaniesV4,
  createClaimDigitalExitV4,
  createMissionDigitalExitV4,
  putClaimEventV4,
  dispatchMissionV4,
  updateMissionV4,
  updateMissionV5,
  updateMissionTypeV4,
  getTowLimitsV4,
  getRosLimitsV4,
  getRosLimitsV5,
  startMissionMonitoringV4,
  npsEligibilityClaim,
  putNpsEligibilityClaimV4,
  requestOTP,
  getJWT,
  getTrustedJWT,
  digitalInteractionsV5,
  createMissionV5,
  createRentalMissionV4,
  createRentalMissionV5,
  updateRentalMissionV4,
  updateRentalMissionV5,
  extendRentalMission,
  getAllMissionsV5,
  cancelMissionV5,
  cancelMissionCostV5,
  automaticCancelMissionV5,
  putVehicleDestinationV5,
  startMissionMonitoringV5,
  getCarDestinationsV5,
  getSpecificCarDestinationsV5,
  putClaimLocationV5,
  searchClaimV5,
  searchClaimByIdV5,
  npsEligibilityClaimV5,
  putNpsEligibilityClaimV5,
  getBrandSuggestionsV5,
  getModelSuggestionsV5,
  getHighwaySectionsV5,
  searchVehicleInfoV5,
  putClaimVehicleLossV5,
  getInsuranceCompaniesV5,
  createClaimDigitalExitV5,
  createMissionDigitalExitV5,
  createClaimV5,
  putClaimEventV5,
  dispatchMissionV5,
  updateMissionTypeV5,
  getTowLimitsV5,
  requestOTPV5,
  getJWTV5,
  getTrustedJWTV5,
  getProductConfigurationV4,
  getProductConfigurationV5,
  getClaimHistoryV4,
  getClaimHistoryV5,
  checkCscEligibilityV4,
  checkCscEligibilityV5,
  updateImmobilizationGarageV4,
  updateImmobilizationGarageV5,
  rentalCurrentLimitsV4,
  rentalCurrentLimitsV5,
  rentalExtensionInfo,
};

const error = method => () => {
  throw new Error(`Unkown method: ${method}`);
};
const main = ({ method, ...rest }) => (methods[method] || error(method))(rest);
compose(
  forEach(([name, fn]) => (main[name] = fn)),
  toPairs,
)(methods);
main.endpoints = compose(
  fromPairs,
  map(([k]) => [k, k]),
  toPairs,
)(methods);

export default main;
